import { JsonApiDataInterface } from './data.interface';
import { JsonApiModelMappingInterface } from './model-mapping.interface';
import { JsonApiModel } from './model';
import { JsonApiPayloadInterface } from './payload.interface';
import { JsonApiSyncedInterface } from './synced.interface';
import { JsonApiModelInterface } from './model.interface';
import { JsonApiCacheInterface } from './cache.interface';

/**
 * Fork of beauby/jsonapi-datastore@0.4.0-beta @JsonApiDataStore
 *
 * Unfortunately, a TypeScript class cannot extend a JavaScript ES6 class,
 * so we had to fork & rewrite the library in TypeScript.
 *
 * @url https://github.com/beauby/jsonapi-datastore/
 */
export class JsonApiStore<Model extends JsonApiModelInterface> {
  /**
   * Cached values in memory
   */
  private cache: JsonApiCacheInterface = {};

  /**
   * Constructor
   *
   * @param mapping - Mapping "resource type" => "model class"
   * @param jsonApiType - Primary JsonApiModel's resource type handled by this store, ex: 'property', 'location', ...
   */
  constructor(private mapping: JsonApiModelMappingInterface, private jsonApiType: string) {
    // Initialize models cache
    this.reset();
  }

  /**
   * Add a model to the store
   *
   * @param model The model to add
   */
  public add(model: Model | JsonApiModel): void {
    if (!this.cache[model.jsonApiType]) {
      this.cache[model.jsonApiType] = {
        model: {},
        order: [],
      };
    }

    this.cache[model.jsonApiType].model[model.id] = model;
    this.cache[model.jsonApiType].order.push(model.id);
  }

  /**
   * Remove a model from the store
   *
   * @param model The model to destroy
   */
  public destroy(model: Model | JsonApiModel): void {
    if (!this.cache[model.jsonApiType]) {
      return;
    }

    // Remove from model
    delete this.cache[model.jsonApiType].model[model.id];

    // Remove from order
    this.cache[model.jsonApiType].order = this.cache[model.jsonApiType].order.filter((id) => id !== model.id);
  }

  /**
   * Retrieve a model of the primary json api resource type, identified id
   *
   * @param id The id of the model
   */
  public find(id: string): Model | null {
    return <Model>this.cache[this.jsonApiType].model[id] || null;
  }

  /**
   * Retrieve all models of the primary json api resource type
   */
  public findAll(): Model[] {
    return this.cache[this.jsonApiType].order.map((id) => this.find(id));
  }

  /**
   * Empty the store
   */
  public reset(): void {
    this.cache = {
      [this.jsonApiType]: {
        model: {},
        order: [],
      },
    };
  }

  /**
   * Initialize a model and returns it
   *
   * @param jsonApiType
   * @param id
   */
  public initModel(jsonApiType: string, id: string): JsonApiModelInterface {
    // Model exists
    if (this.cache[jsonApiType] && this.cache[jsonApiType].model[id]) {
      return this.cache[jsonApiType].model[id];
    }

    let model: Model | JsonApiModel;

    // Generic JSON API model
    if (!this.mapping[jsonApiType]) {
      model = new JsonApiModel();
      model.jsonApiType = jsonApiType;

      // Concrete JSON API model
    } else {
      model = new this.mapping[jsonApiType]();
    }

    // Set ID
    model.id = id;

    // Keep new model in cache
    this.add(model);

    return model;
  }

  /**
   * Synchronize a record with the store
   *
   * @param record
   */
  public syncRecord(record: JsonApiDataInterface): JsonApiModelInterface {
    // Find or instantiate model
    const model = this.initModel(record.type, record.id);

    // Attributes hydrator
    if (!!record.attributes) {
      Object.keys(record.attributes).forEach((key: keyof typeof model) => {
        // key is an attribute of the class of model

        model.setAttribute(key, record.attributes[key]);
      });
    }

    // Relationships hydrator
    if (!!record.relationships) {
      Object.keys(record.relationships).forEach((key: keyof typeof model) => {
        // key is an attribute of the class of model

        model.jsonApiRelationships.push(key);

        const relationshipData = record.relationships[key].data;

        // Relationship is an object or array
        if (!!relationshipData && typeof relationshipData === 'object') {
          // Relationship is an array
          if (relationshipData.constructor === Array) {
            model.setRelationship(
              key,
              (<JsonApiDataInterface[]>relationshipData).map((rec) => this.initModel(rec.type, rec.id))
            );

            // Relationship is an object
          } else {
            const rec = <JsonApiDataInterface>relationshipData;

            model.setRelationship(key, this.initModel(rec.type, rec.id));
          }
          // Relationship is invalid
        } else {
          model.setRelationship(key, null);
        }
      });
    }

    // Links hydrator
    if (!!record.links) {
      model.links = {};

      Object.keys(record.links).forEach((key: keyof typeof model) => {
        // key is an attribute of the class of model

        model.jsonApiLinks.push(key);
        model.links[key] = record.links[key];
      });
    }

    // Meta hydrator
    if (!!record.meta) {
      model.meta = {};

      Object.keys(record.meta).forEach((key) => {
        model.jsonApiMeta.push(key);
        model.meta[key] = record.meta[key];
      });
    }

    return model;
  }

  /**
   * Sync a JSONAPI-compliant payload with the store and return any metadata included in the payload
   *
   * @param payload The JSONAPI payload
   */
  public syncWithMeta(payload: JsonApiPayloadInterface): JsonApiSyncedInterface<Model> {
    let data: Model | Model[] | null = null;

    // Sync included records
    if (payload.included) {
      payload.included.map((record) => this.syncRecord(record));
    }

    if (!!payload.data) {
      if (payload.data.constructor === Array) {
        data = (<JsonApiDataInterface[]>payload.data).map((record) => <Model>this.syncRecord(record));
      } else {
        data = <Model>this.syncRecord(<JsonApiDataInterface>payload.data);
      }
    }

    return {
      data,
      meta: !!payload.meta ? payload.meta : null,
    };
  }

  /**
   * Sync a JSONAPI-compliant payload with the store.
   *
   * @param payload
   */
  public sync(payload: JsonApiPayloadInterface): Model | Model[] | null {
    return this.syncWithMeta(payload).data;
  }
}
